"use client";

import { useEffect, useRef, useState } from "react";
import { SquarePen, X } from "lucide-react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { CommandIcon, useCommandPanel } from "@/components/aios-command-panel";
import { useCommandPrototype } from "@/components/command-team-v2/prototype/store";
import { CommandV2AssistantMessageRow } from "@/components/command-team-v2/chat/command-v2-assistant-message-row";
import { CommandV2AssistantHistory } from "@/components/command-team-v2/chat/command-v2-assistant-history";
import { CommandThreadSkeleton } from "@/components/command-team-v2/chat/command-v2-loading-skeletons";
import { CommandV2AssistantComposer } from "@/components/command-team-v2/composer/command-v2-assistant-composer";

const WIDTH_STORAGE_KEY = "aios:command-sidebar-width";
const DRAWER_SESSION_KEY = "aios:command-v2-drawer-session";
const DEFAULT_WIDTH = 400;
const MIN_WIDTH = 340;
const MAX_WIDTH = 720;

function clampWidth(width: number) {
    return Math.min(Math.max(width, MIN_WIDTH), Math.min(MAX_WIDTH, window.innerWidth - 64));
}

export function CommandV2AssistantPanel() {
    const { isOpen, close } = useCommandPanel();
    const { state, actions, isLoadingThread } = useCommandPrototype();
    const [tab, setTab] = useState<"chat" | "history">("chat");
    const [drawerSessionId, setDrawerSessionId] = useState<string | null>(null);
    const [drawerProjectId, setDrawerProjectId] = useState<string | null>(null);
    const [width, setWidth] = useState(DEFAULT_WIDTH);
    const [resizing, setResizing] = useState(false);
    const widthRestored = useRef(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    const drawerSession = drawerSessionId
        ? state.sessions.find((session) => session.id === drawerSessionId) ?? null
        : null;
    const messages = drawerSession?.messages ?? [];

    useEffect(() => {
        try {
            const saved = Number(localStorage.getItem(WIDTH_STORAGE_KEY));
            if (saved >= MIN_WIDTH) setWidth(clampWidth(saved));
            const savedSession = sessionStorage.getItem(DRAWER_SESSION_KEY);
            if (savedSession) setDrawerSessionId(savedSession);
        } catch {
            /* ignore */
        }
        widthRestored.current = true;
    }, []);

    useEffect(() => {
        if (!widthRestored.current) return;
        try {
            localStorage.setItem(WIDTH_STORAGE_KEY, String(width));
        } catch {
            /* ignore */
        }
    }, [width]);

    useEffect(() => {
        try {
            if (drawerSessionId) sessionStorage.setItem(DRAWER_SESSION_KEY, drawerSessionId);
            else sessionStorage.removeItem(DRAWER_SESSION_KEY);
        } catch {
            /* ignore */
        }
    }, [drawerSessionId]);

    useEffect(() => {
        if (!isOpen) {
            document.body.style.removeProperty("--command-width");
            return;
        }
        document.body.style.setProperty("--command-width", `${width}px`);
        return () => {
            document.body.style.removeProperty("--command-width");
        };
    }, [isOpen, width]);

    useEffect(() => {
        if (!resizing) return;
        document.body.dataset.commandResizing = "true";
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        return () => {
            delete document.body.dataset.commandResizing;
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
    }, [resizing]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages.length, tab, isOpen]);

    useEffect(() => {
        if (isOpen && tab === "chat" && drawerSession?.unread) {
            actions.markChatRead(drawerSession.id);
        }
    }, [isOpen, tab, drawerSession?.unread, drawerSession?.id, actions]);

    if (!isOpen) return null;

    return (
        <aside
            style={{ top: "var(--command-top, 3rem)" }}
            className={cn(
                "fixed bottom-0 right-0 z-40 flex w-full flex-col border-l bg-background shadow-xl animate-in slide-in-from-right duration-300 sm:w-[min(var(--command-width,400px),100vw)]",
                !resizing && "sm:transition-[width]"
            )}
        >
            <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize Command sidebar"
                onPointerDown={(event) => {
                    event.preventDefault();
                    event.currentTarget.setPointerCapture(event.pointerId);
                    setResizing(true);
                }}
                onPointerMove={(event) => {
                    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
                    setWidth(clampWidth(window.innerWidth - event.clientX));
                }}
                onPointerUp={(event) => {
                    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                        event.currentTarget.releasePointerCapture(event.pointerId);
                    }
                    setResizing(false);
                }}
                onPointerCancel={() => setResizing(false)}
                onLostPointerCapture={() => setResizing(false)}
                onDoubleClick={() => setWidth(DEFAULT_WIDTH)}
                className={cn(
                    "absolute inset-y-0 -left-1 z-10 hidden w-2.5 cursor-col-resize touch-none transition-colors sm:block",
                    resizing ? "bg-primary/30" : "hover:bg-primary/20"
                )}
            />

            <Tabs
                value={tab}
                onValueChange={(value) => setTab(value as "chat" | "history")}
                className="flex min-h-0 flex-1 flex-col"
            >
            <TooltipProvider delayDuration={400}>
                <div className="flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2.5">
                    <TabsList>
                        <TabsTrigger value="chat">chat</TabsTrigger>
                        <TabsTrigger value="history">history</TabsTrigger>
                    </TabsList>
                    <div className="flex items-center gap-1">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setDrawerSessionId(null);
                                        setDrawerProjectId(null);
                                        setTab("chat");
                                    }}
                                    className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                    aria-label="New chat"
                                >
                                    <SquarePen className="size-4" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="text-xs">
                                New chat
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    onClick={close}
                                    className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                    aria-label="Close Command"
                                >
                                    <X className="size-4" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="text-xs">
                                Close
                            </TooltipContent>
                        </Tooltip>
                    </div>
                </div>
            </TooltipProvider>

            <TabsContent value="chat" className="mt-0 min-h-0 flex-1 data-[state=active]:flex data-[state=active]:flex-col">
                <div ref={scrollRef} className="app-scrollbar min-h-0 flex-1 overflow-y-auto px-4 py-4">
                    <div className="flex min-h-full flex-col justify-end gap-4">
                        {drawerSessionId &&
                        messages.length === 0 &&
                        isLoadingThread &&
                        state.activeSessionId === drawerSessionId ? (
                            <CommandThreadSkeleton />
                        ) : messages.length === 0 ? (
                            <div className="mb-auto flex flex-1 flex-col items-center justify-center pt-6 text-center">
                                <span className="mx-auto flex size-10 items-center justify-center rounded-full bg-primary/10">
                                    <CommandIcon className="size-5 text-primary" />
                                </span>
                                <p className="mt-3 text-sm font-semibold">Talk to Command</p>
                                <p className="mx-auto mt-1 max-w-[260px] text-sm text-muted-foreground">
                                    Ask anything about your workspace — chats started here land
                                    in your Command history, so you can pick them up on the
                                    COMMAND tab any time.
                                </p>
                            </div>
                        ) : null}
                        {messages.map((message) =>
                            message.role === "user" ? (
                                <div
                                    key={message.id}
                                    className="ml-auto w-fit max-w-[85%] rounded-2xl bg-muted px-4 py-2.5 text-sm leading-relaxed"
                                >
                                    {message.content}
                                </div>
                            ) : (
                                <CommandV2AssistantMessageRow
                                    key={message.id}
                                    message={message}
                                />
                            )
                        )}
                    </div>
                </div>
            </TabsContent>
            <TabsContent value="history" className="mt-0 min-h-0 flex-1 data-[state=active]:flex data-[state=active]:flex-col">
                <CommandV2AssistantHistory
                    activeSessionId={drawerSessionId}
                    onOpenChat={(sessionId) => {
                        setDrawerSessionId(sessionId);
                        setDrawerProjectId(null);
                        setTab("chat");
                    }}
                    onNewChatInProject={(projectId) => {
                        setDrawerSessionId(null);
                        setDrawerProjectId(projectId);
                        setTab("chat");
                    }}
                    onClose={close}
                />
            </TabsContent>

            <CommandV2AssistantComposer
                sessionId={drawerSessionId}
                projectId={drawerSession?.projectId ?? drawerProjectId}
                onSent={(sessionId) => {
                    if (sessionId) setDrawerSessionId(sessionId);
                    setTab("chat");
                }}
                onNewChat={() => {
                    setDrawerSessionId(null);
                    setTab("chat");
                }}
            />
            </Tabs>
        </aside>
    );
}
